import { useEffect, useState } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { Input } from "components/ui/inputs/Input"
import { TOnChangeInput } from "types/default.type"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useRenameQuotation } from "hooks/api/quotation/quotations.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { useMessage } from "hooks/message.hook"
import { useQueryClient } from "@tanstack/react-query"
import style from "./RenameQuotationModal.module.scss"

const { inputBlock, buttonsBlock, label } = style

type Props = {
	isModalOpen: boolean
	setModalClose: () => void
	quotationId: number | null
	quotationName: string
}

export const RenameQuotationModal = ({ isModalOpen, setModalClose, quotationId, quotationName }: Props) => {
	const [inputValue, setInputValue] = useState("")
	const { mutate: renameQuotationMutation, isPending: isRenamingPending } = useRenameQuotation()
	const { success: successMessage, error: errorMessage } = useMessage()
	const queryClient = useQueryClient()

	// Set input value to current quotation name when modal opens
	useEffect(() => {
		if (isModalOpen && quotationName) {
			setInputValue(quotationName)
		}
	}, [isModalOpen, quotationName])

	const onChangeHandle = (e: TOnChangeInput) => {
		setInputValue(e.target.value)
	}

	const onSaveHandle = () => {
		if (quotationId !== null) {
			renameQuotationMutation(
				{ quotationId, name: inputValue },
				{
					onSuccess: () => {
						setModalClose()
						setInputValue("")
						successMessage("Quotation renamed successfully")
						queryClient.invalidateQueries({ queryKey: ["quotations"] })
					},
					onError: () => {
						errorMessage("Failed to rename quotation, please try again")
					},
				},
			)
		}
	}

	return (
		<Modal title="Rename quotation" isModalOpen={isModalOpen} width={456} setModalClose={setModalClose}>
			{isRenamingPending ? (
				<Skeleton rows={6} />
			) : (
				<>
					<div className={inputBlock}>
						<span className={label}>Quotation Name</span>
						<Input value={inputValue} onChange={onChangeHandle} />
					</div>
					<div className={buttonsBlock}>
						<ButtonSecondary onClick={() => setModalClose()}>Cancel</ButtonSecondary>
						<ButtonPrimary onClick={() => onSaveHandle()} submittable={!!inputValue}>
							Save
						</ButtonPrimary>
					</div>
				</>
			)}
		</Modal>
	)
}
